import {
  useBrowseProductEntitlementApi,
  useBrowseSubscriptionPlanApi,
} from '@aibees/shared/api';
import {
  PlanInterval,
  PlanSlug,
  type PlanCardConfig,
} from '@aibees/shared/definition';
import { useMemo } from 'react';
import { buildPlanCardConfig } from '../catalog';

const PLAN_SLUGS = [
  PlanSlug.FREE,
  PlanSlug.STARTER,
  PlanSlug.BUILDER,
  PlanSlug.SCALE,
] as const;

export const USAGE_BASED_SLUG = PlanSlug.BUILDER;

export type PlanConfigsBySlug = Record<PlanSlug, PlanCardConfig | null>;

export interface PlanConfigs {
  bySlug: PlanConfigsBySlug;
  byName: Record<string, PlanCardConfig>;
  isLoading: boolean;
}

const EMPTY_BY_SLUG: PlanConfigsBySlug = {
  [PlanSlug.FREE]: null,
  [PlanSlug.STARTER]: null,
  [PlanSlug.BUILDER]: null,
  [PlanSlug.SCALE]: null,
};

export function usePlanConfigs(): PlanConfigs {
  const { data: catalog, isLoading: catalogLoading } =
    useBrowseProductEntitlementApi();
  const { data: monthlyPlans, isLoading: plansLoading } =
    useBrowseSubscriptionPlanApi(PlanInterval.MONTH);

  const isLoading = catalogLoading || plansLoading;

  return useMemo(() => {
    if (!catalog || !monthlyPlans) {
      return { bySlug: EMPTY_BY_SLUG, byName: {}, isLoading };
    }

    const bySlug: PlanConfigsBySlug = { ...EMPTY_BY_SLUG };
    const byName: Record<string, PlanCardConfig> = {};

    for (const slug of PLAN_SLUGS) {
      const product = catalog.products.find((p) => p.metadata.slug === slug);
      if (!product) continue;
      const config = buildPlanCardConfig(product.id, catalog, monthlyPlans);
      bySlug[slug] = config;
      if (config) byName[product.name] = config;
    }

    return { bySlug, byName, isLoading };
  }, [catalog, monthlyPlans, isLoading]);
}
